/* eslint-disable @typescript-eslint/no-explicit-any */
import { getBasicToken } from '@/utils/authUtils';
import moment from 'moment';
import { INexudusCoworker, INexudusCoworkerParams } from '@/interfaces/nexudus/INexudusCoworker';
import { INexudusTeam } from '@/interfaces/nexudus/INexudusTeam';
import IBusinessLocation from '@/app/(auth)/signup/types/IBusinessLocation';
import { INexudusTokenPayload } from '@/interfaces/nexudus/INexudusTokenPayload';
import { INexudusRefreshTokenPayload } from '@/interfaces/nexudus/INexudusRefreshTokenPayload';
import { INexudusService } from '@/services/nexudus/INexudusService';
import { INexudusUser } from '@/interfaces/nexudus/INexudusUser';
import { logrocketService } from '@/services/logrocket';
import { INexudusCoworkerSummary } from '@/interfaces/nexudus/INexudusCoworkerSummary';
import { IUserProfile } from '@/interfaces/auth/IUserProfile';
import { tryParseJSON } from '@/utils/tryParseJSON';
import { z } from 'zod';
import { RouteError } from '@/utils/RouteError';
import { INexudusInvoice } from '@/interfaces/nexudus/INexudusInvoice';
import {
  IFormAnswersResponse,
  IFormPageData,
  IFormPageRequestResponse,
  IFormQuestionsData,
} from '@/interfaces/nexudus/IFormData';
import FormData from 'form-data';
import { trace } from '@opentelemetry/api';
import { IFetchHelper } from '@/interfaces/helper/IFetchHelper';
import { FetchHelper } from '@/utils/helper/FetchHelper';
import { RetryingFetchHelper } from '@/services/nexudus/RetryingFetchHelper';
import { logger } from '@/logger';
import { ServerErrorLogger } from '@/utils/ServerError';
import { INexudusCoworkerContract } from '@/interfaces/nexudus/INexudusCoworkerContract';
import { INexudusBusinessNetworks } from '@/interfaces/nexudus/INexudusBusinessNetworks';
import {
  INexudusCoworkerTask,
  NexudusCoworkerTaskListResponseSchema,
} from '@/interfaces/nexudus/INexudusCoworkerTask';
import { IExtraServices } from '@/interfaces/nexudus/IExtraServices';
import { NexudusResponseChecker } from '@/services/nexudus/NexudusResponseChecker';
import { DateTime } from 'luxon';

export interface INexudusConfig {
  envBaseUrl: string;
  apiBaseUrl: string;
  redirectToUrl: string;
  networkBusinessId: number;
  planExpirationDuration?: number;
  freePlanTariffId: number;
  viewingsPlanTariffId: number;
  hostRoleId: number;
}

interface INewsLetterSignupFields {
  Custom3: boolean;
  SignUpToNewsletter: boolean;
  CustomFields: { Data: [{ Name: string; Value: string }] };
}

const NEXUDUS_LONG_REQUEST_DURATION = 20000; // 20 seconds
const NEXUDUS_CUSTOMER_DISCOUNT_AMOUNT = 25; // 25%

const NEXUDUS_ERROR_SCHEMA = z.object({
  Message: z.string(),
  Status: z.number().optional(),
});

export class NexudusService implements INexudusService {
  readonly config: INexudusConfig;
  public readonly logger = logger.child({ service: 'NexudusService' });
  private readonly errorLogger = ServerErrorLogger.forLogger(this.logger, 'NexudusError');

  private readonly checker: NexudusResponseChecker;
  constructor(
    config: INexudusConfig,
    private readonly fetchHelper: IFetchHelper = new RetryingFetchHelper(new FetchHelper()),
  ) {
    this.config = config;
    this.checker = new NexudusResponseChecker(this.fetchHelper, this.errorLogger);
  }

  static coworkerFromUser(user: IUserProfile, overrideConfig?: INexudusConfig): INexudusCoworkerParams {
    const config = overrideConfig || NexudusService.defaultConfig();

    const businesses = [config.networkBusinessId];

    if (user.homeLocation && user.homeLocation !== config.networkBusinessId) {
      businesses.push(user.homeLocation);
    }

    const newsLetterSignupFields = NexudusService.loadNewsletterConsentProperties(user.hasMarketingConsent);
    return {
      FullName: user.fullName || user.firstName + ' ' + user.lastName,
      Email: user.email,
      MobilePhone: user.mobilePhone,
      Address: user.address,
      CityName: user.city,
      State: user.state,
      PostCode: user.postCode,
      Custom1: user.accountType,
      Salutation: user.salutation,
      BusinessName: user.businessName || user.tradingName || '',
      CompanyName: user.businessName || user.tradingName || '',
      Custom2: user.vatNumber || '',
      CountryId: 1220,
      SimpleTimeZoneId: 2023,
      Teams: user.team?.nexudusId ? [user.team.nexudusId] : [],
      Businesses: businesses,
      InvoicingBusinessId: user.homeLocation,
      BillingEmail: user.email,
      BillingName: user.businessName || user.tradingName,
      BillingAddress: user.address,
      BillingCityName: user.city,
      BillingState: user.state,
      BillingPostCode: user.postCode,
      GeneralTermsAccepted: true,
      CreateUser: true,
      CoworkerType: 1,
      TaxIDNumber: user.vatNumber,
      ...newsLetterSignupFields,
      Custom3: newsLetterSignupFields.Custom3 ? 'true' : 'false',
    };
  }

  static loadNewsletterConsentProperties = (isUserSignedUpToNewsletter: boolean): INewsLetterSignupFields => {
    // id can be found by calling a coworker (https://developers.nexudus.com/reference/get-coworker)
    const customFieldIdForMarketingConsent = process.env.MARKETING_CONSENT_ID!;
    return {
      // newsletter data stored in Nexudus custom field
      Custom3: isUserSignedUpToNewsletter,
      // sets user to 'news letter' subscriber group in Nexudus
      SignUpToNewsletter: isUserSignedUpToNewsletter,
      // field id (name) and value for the Nexudus dashboard custom field being used for "Marketing Consent" in coworker profile account dashboard
      CustomFields: {
        Data: [{ Name: customFieldIdForMarketingConsent, Value: `${isUserSignedUpToNewsletter}` }],
      },
    };
  };

  async createRawCoworkerAndUser(coworker: Omit<INexudusCoworker, 'Id' | 'UserId'>) {
    const url = `${this.config.apiBaseUrl}/api/spaces/coworkers`;
    const accessToken: string = getBasicToken();
    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
        body: JSON.stringify(coworker),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }

      return (await response.json()).Value;
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error creating coworker and user')
        .logAndThrowRouteError();
    }
  }

  async createCoworkerAndUser(user: IUserProfile): Promise<INexudusCoworker> {
    const url = `${this.config.apiBaseUrl}/api/spaces/coworkers`;
    const accessToken: string = getBasicToken();
    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
        body: JSON.stringify(NexudusService.coworkerFromUser(user, this.config)),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }

      return (await response.json()).Value;
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error creating coworker and user')
        .logAndThrowRouteError();
    }
  }

  async createUser(user: Omit<INexudusUser, 'Id'>): Promise<Pick<INexudusUser, 'Id'>> {
    const url = `${this.config.apiBaseUrl}/api/sys/users`;
    const accessToken: string = getBasicToken();
    const response = await this.checker.requestWithErrorHandling<{ Value: INexudusUser }>(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Basic ${accessToken}`,
      },
      body: JSON.stringify(user),
    });

    return response.Value;
  }

  async deleteTeam(teamId: number): Promise<void> {
    const url = `${this.config.apiBaseUrl}/api/spaces/teams/${teamId}`;
    const accessToken: string = getBasicToken();
    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });
      if (!response.ok) {
        const e = new Error(`Request failed with status: ${response.status} ${response.statusText}`);
        logrocketService.logException(e);
        throw e;
      }
      const body = await response.json();

      if (body.WasSuccessful) {
        return body.Value;
      } else {
        throw new Error(`Failed to delete team: ${body.Errors}`);
      }
    } catch (error: unknown) {
      return this.errorLogger.fromError(error).withMessage(`Error deleting team`).logAndThrowRouteError();
    }
  }
  async createTeam(teamName: string, businessId: number): Promise<INexudusTeam> {
    const url = `${this.config.apiBaseUrl}/api/spaces/teams`;
    const accessToken: string = getBasicToken();
    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
        body: JSON.stringify({
          Name: teamName,
          BusinessId: businessId,
          DiscountExtraServices: NEXUDUS_CUSTOMER_DISCOUNT_AMOUNT,
        }),
      });
      if (!response.ok) {
        const e = new Error(`Request failed with status: ${response.status} ${response.statusText}`);
        logrocketService.logException(e);
        throw e;
      }
      const body = await response.json();

      if (body.WasSuccessful) {
        return body.Value;
      } else {
        throw new Error(`Failed to create team: ${body.Errors}`);
      }
    } catch (error: unknown) {
      return this.errorLogger.fromError(error).withMessage(`Error creating team`).logAndThrowRouteError();
    }
  }

  async getBusiness() {
    const url = `${this.config.envBaseUrl}/en/business`;
    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error fetching business data')
        .logAndThrowRouteError();
    }
  }

  async getBusinessLocations(): Promise<IBusinessLocation[]> {
    const url = `${this.config.envBaseUrl}/en/business/all?_depth=1&includeroot=true`;
    const accessToken: string = getBasicToken();
    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error fetching business locations')
        .logAndThrowRouteError();
    }
  }

  async getUserForEmail(email: string): Promise<INexudusUser | undefined> {
    const url = new URL(`${this.config.apiBaseUrl}/api/sys/users`);
    url.searchParams.set('User_Email', email);
    url.searchParams.set('page', '1');
    url.searchParams.set('size', '1');
    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Basic ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }
      const responseText = await response.text();
      const body = JSON.parse(responseText);

      // Find a user with an email that matches the request
      if (body.Records.length === 0) return undefined;
      return body.Records.find((user: INexudusUser) => user.Email.toLowerCase() === email.toLowerCase());
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error fetching user for email')
        .logAndThrowRouteError();
    }
  }

  async getTeam(teamName: string): Promise<INexudusTeam | null> {
    const url = new URL(`${this.config.apiBaseUrl}/api/spaces/teams`);
    url.searchParams.set('Team_Name', teamName);
    const accessToken: string = getBasicToken();
    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Basic ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }
      const json = await response.json();
      if (json.Records.length > 1) {
        return (
          json.Records.find((team: INexudusTeam) => team.Name?.toLowerCase() === teamName?.toLowerCase()) ||
          null
        );
      } else if (json.Records.length === 1) {
        return json.Records[0];
      }
      return null;
    } catch (error) {
      return this.errorLogger.fromError(error).withMessage('Error fetching team').logAndThrowRouteError();
    }
  }

  static defaultConfig() {
    return {
      envBaseUrl: process.env.NEXUDUS_ENV_BASE_URL as string,
      apiBaseUrl: process.env.NEXUDUS_API_BASE_URL as string,
      redirectToUrl: process.env.NEXUDUS_REDIRECT_TO_URL as string,
      networkBusinessId: Number.parseInt(process.env.NEXUDUS_NETWORK_BUSINESS_ID as string, 10),
      planExpirationDuration: Number.parseInt(process.env.PLAN_EXPIRATION_DURATION as string, 10),
      freePlanTariffId: Number.parseInt(process.env.NEXUDUS_FREE_PLAN_TARIFF_ID as string, 10),
      viewingsPlanTariffId: Number.parseInt(process.env.NEXUDUS_VIEWINGS_PLAN_TARIFF_ID as string, 10),
      hostRoleId: Number.parseInt(process.env.NEXUDUS_HOST_ROLE_ID as string, 10),
    };
  }

  static throwIfNexudusError(responseBody: z.input<typeof NEXUDUS_ERROR_SCHEMA>) {
    const parsed = NEXUDUS_ERROR_SCHEMA.safeParse(responseBody);

    if (!parsed.success) {
      return; // not a nexudus error - ignore
    }

    if (parsed.data.Status !== 200) {
      throw new RouteError({ error: `NexudusError: ${parsed.data.Message}` }, parsed.data.Status);
    }
  }

  async getOneTimeTokenForUserId(userId: number, clientId: string): Promise<string> {
    const url = new URL(`${this.config.apiBaseUrl}/api/sys/users/${userId}/token/refresh`);

    const accessToken: string = getBasicToken();
    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
          client_id: clientId,
        },
      });

      if (!response.ok) {
        const e = new Error(`Request failed with status: ${response.status} ${response.statusText}`);
        logrocketService.logException(e);
        throw e;
      }

      const responseBody = await response.json();

      const schema = z.object({
        Value: z.string({ required_error: "Missing required field 'Value' in nexudus response" }),
      });
      const parsed = schema.safeParse(responseBody);

      if (!parsed.success) {
        this.logger.error(
          {
            parsedError: parsed.error,
            responseBody,
          },
          `Failed to parse Nexudus response`,
        );
        throw new Error(`Failed to parse Nexudus response: ${parsed.error}`);
      }
      return parsed.data.Value;
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error fetching token for user')
        .logAndThrowRouteError();
    }
  }

  async exchangeToken(token: string, clientId: string): Promise<INexudusTokenPayload> {
    const url = new URL(`${this.config.apiBaseUrl}/api/sys/users/exchange`);

    url.searchParams.set('token', token);

    const startedAt = new Date();
    const response = await this.fetchHelper.fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        client_id: clientId,
      },
    });

    if (!response.ok) {
      return this.errorLogger
        .fromMessage(`Failed to exchange Nexudus token: ${response.status} ${response.statusText}`)
        .logAndThrowRouteError();
    }

    let responseBody;
    try {
      responseBody = await response.json();
      const endedAt = new Date();

      const duration = endedAt.getTime() - startedAt.getTime();
      if (duration > NEXUDUS_LONG_REQUEST_DURATION) {
        this.logger.warn(`Nexudus exchange token request took ${duration}ms`);
      }
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error fetching token for user')
        .logAndThrowRouteError();
    }

    NexudusService.throwIfNexudusError(responseBody); // Throw a nexudus error that's parsed properly

    const responseSchema: z.ZodType<INexudusTokenPayload> = z.object({
      refresh_token: z.string(),
      expires_in: z.coerce.number(),
      token: z.string(),
      forms_auth: z.string(),
    });

    const parseResult = responseSchema.safeParse(responseBody);

    if (!parseResult.success) {
      const errorString = parseResult.error.errors.map(error => `${error.path}: ${error.message}`).join(', ');
      logger.error(
        {
          errorString,
          responseBody,
        },
        'Failed to parse Nexudus token response',
      );
      return this.errorLogger.fromMessage(`Failed to parse Nexudus token response`).logAndThrowRouteError();
    }
    return parseResult.data;
  }

  async refreshToken(refreshToken: string, clientId: string): Promise<INexudusRefreshTokenPayload> {
    // Sends a POST to https://spaces.nexudus.com/api/token
    // with the following body:
    // grant_type: refresh_token
    // refresh_token: <refreshToken>
    // client_id: process.env.NEXUDUS_USERNAME

    const details = {
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    };
    const formBody = new URLSearchParams(details).toString();

    const url = `${this.config.apiBaseUrl}/api/token`;

    const requestStart = new Date();

    const response = await trace.getTracer('nexudus').startActiveSpan('refreshToken', async span => {
      try {
        return await this.fetchHelper.fetch(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
            client_id: clientId,
          },
          body: formBody,
        });
      } finally {
        span.end();
      }
    });

    if (!response.ok) {
      const responseBody = await response.text();
      this.logger.error(
        {
          status: response.status,
          statusText: response.statusText,
          responseBody,
        },
        `Failed to refresh Nexudus token`,
      );

      return this.errorLogger.fromMessage('Failed to refresh Nexudus token').logAndThrowRouteError();
    }

    const responseBody = await response.json();
    const requestEnd = new Date();

    const duration = requestEnd.getTime() - requestStart.getTime();

    if (duration > NEXUDUS_LONG_REQUEST_DURATION) {
      this.logger.warn(`Nexudus refresh token request took ${duration}ms`);
    }

    const responseSchema: z.ZodType<INexudusRefreshTokenPayload> = z.object({
      refresh_token: z.string(),
      expires_in: z.coerce.number(),
      access_token: z.string(),
      token_type: z.string(),
    });

    try {
      return responseSchema.parse(responseBody);
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Failed to parse Nexudus refresh token response')
        .logAndThrowRouteError();
    }
  }

  async getCoworker(coworkerId: number): Promise<INexudusCoworker | null> {
    const url = new URL(`${this.config.apiBaseUrl}/api/spaces/coworkers/${coworkerId}`);
    const accessToken: string = getBasicToken();
    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Request failed with status: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }

      return response.json();
    } catch (error) {
      return this.errorLogger.fromError(error).withMessage('Error fetching coworker').logAndThrowRouteError();
    }
  }

  async updateCoworker(coworker: Partial<INexudusCoworker>) {
    const url = `${this.config.apiBaseUrl}/api/spaces/coworkers`;
    const accessToken: string = getBasicToken();

    try {
      return await this.checker.requestWithErrorHandling<INexudusCoworker>(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
        body: JSON.stringify(coworker),
      });
    } catch (error) {
      return this.errorLogger.fromError(error).withMessage('Error updating coworker').logAndThrowRouteError();
    }
  }

  async getCoworkerByUserId(userId: number): Promise<INexudusCoworkerSummary | undefined> {
    // assumes only one coworker per user
    try {
      const url = new URL(`${this.config.apiBaseUrl}/api/spaces/coworkers`);
      url.searchParams.set('Coworker_User', userId.toString());
      const accessToken: string = getBasicToken();

      const result = await this.checker.requestWithErrorHandling<{ Records: INexudusCoworkerSummary[] }>(
        url.toString(),
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Basic ${accessToken}`,
          },
        },
      );

      return result.Records[0];
    } catch (error) {
      return this.errorLogger.fromError(error).withMessage('Error fetching coworker').logAndThrowRouteError();
    }
  }

  async getUserToken(userId: number) {
    try {
      const accessToken: string = getBasicToken();

      const url = `${this.config.apiBaseUrl}/api/sys/users/${userId}/token/refresh`;

      const response = await this.fetchHelper.fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Request failed with status: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }

      const result = await response.json();
      return await result.Value;
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error exchanging user token')
        .logAndThrowRouteError();
    }
  }

  async addCoworkerToPlan(
    coworkerId: number,
    tariffId: number = this.config.freePlanTariffId,
  ): Promise<INexudusCoworkerContract> {
    try {
      const url = `${this.config.apiBaseUrl}/api/billing/coworkerContracts`;
      const accessToken: string = getBasicToken();

      const requestData = {
        TypeName: 'coworkerContract',
        IssuedById: parseInt(process.env.NEXUDUS_NETWORK_BUSINESS_ID!),
        CoworkerId: coworkerId,
        TariffId: tariffId,
        StartDate: moment(),
        Quantity: 1,
        BillingDay: 14,
      };

      return (
        await this.checker.requestWithErrorHandling<{ Value: INexudusCoworkerContract }>(url, {
          method: 'POST',
          headers: {
            Authorization: `Basic ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        })
      ).Value;
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error adding user to plan')
        .logAndThrowRouteError();
    }
  }

  async getActiveFreePlanContracts() {
    try {
      const accessToken: string = getBasicToken();

      const url = new URL(
        `${this.config.apiBaseUrl}/api/billing/coworkercontracts?CoworkerContract_Tariff=${this.config.freePlanTariffId}&CoworkerContract_Active=true`,
      );

      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Basic ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Request failed with status: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }

      const { Records } = await response.json();
      return Records;
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error getting active free plan contracts')
        .logAndThrowRouteError();
    }
  }
  async getActiveContractsForCoworkerId(
    coworkerId: number,
    tariffId: number = this.config.freePlanTariffId,
  ): Promise<INexudusCoworkerContract[]> {
    try {
      const accessToken: string = getBasicToken();

      const url = new URL(
        `${this.config.apiBaseUrl}/api/billing/coworkercontracts?CoworkerContract_Tariff=${tariffId}&CoworkerContract_Active=true&CoworkerContract_Coworker=${coworkerId}`,
      );

      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Basic ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Request failed with status: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }

      const { Records } = await response.json();
      return Records;
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error getting active free plan contracts')
        .logAndThrowRouteError();
    }
  }

  /**
   * Gets all the active contracts for a coworker.
   *
   * Unlike `getActiveContractsForCoworkerId`, this method does not filter by tariff ID.
   * @param coworkerId
   */
  async getAllActiveContractsForCoworkerId(coworkerId: number): Promise<INexudusCoworkerContract[]> {
    try {
      const accessToken: string = getBasicToken();

      const url = new URL(
        `${this.config.apiBaseUrl}/api/billing/coworkercontracts?CoworkerContract_Active=true&CoworkerContract_Coworker=${coworkerId}`,
      );

      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Basic ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Request failed with status: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }

      const { Records } = await response.json();
      return Records;
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error getting active free plan contracts')
        .logAndThrowRouteError();
    }
  }

  async removeUserFromPlanByIds(contractIds: number[]): Promise<void> {
    try {
      const accessToken: string = getBasicToken();

      const url = `${this.config.apiBaseUrl}/api/billing/coworkercontracts/runcommand`;

      const requestData = { Key: 'CANCEL_CONTRACT', Ids: contractIds };

      const response = await this.fetchHelper.fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Request failed with status: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error removing user from plan')
        .logAndThrowRouteError();
    }
  }

  async setCoworkerLastActive(coworker: INexudusCoworker): Promise<void> {
    try {
      const accessToken: string = getBasicToken();

      const url = `${this.config.apiBaseUrl}/api/spaces/coworkers`;

      coworker.Custom5 = moment().utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]');

      const result = await this.fetchHelper.fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Basic ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(coworker),
      });

      if (!result.ok) {
        return this.errorLogger
          .fromMessage(`Request failed with status: ${result.status} ${result.statusText}`)
          .logAndThrowRouteError();
      }
    } catch (error) {
      // We don't want to actually break on this error, as it's not critical
      this.errorLogger.fromError(error).withMessage('Error setting coworker last active').handle();
    }
  }

  async getCoworkers(
    params: Record<string, string | number>,
  ): Promise<{ coworkers: INexudusCoworkerSummary[]; hasMore: boolean }> {
    try {
      const accessToken: string = getBasicToken();

      const url = new URL(`${this.config.apiBaseUrl}/api/spaces/coworkers`);
      const urlParmas = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => urlParmas.append(key, value.toString()));
      url.search = urlParmas.toString();

      const result = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Basic ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!result.ok) {
        return this.errorLogger
          .fromMessage(`Request failed with status: ${result.status} ${result.statusText}`)
          .logAndThrowRouteError();
      }

      const { Records, HasNextPage } = await result.json();
      return { coworkers: Records, hasMore: !!HasNextPage };
    } catch (error) {
      return this.errorLogger.fromError(error).withMessage('Error getting coworkers').logAndThrowRouteError();
    }
  }

  async getUser(userId: number): Promise<INexudusUser | null> {
    const url = new URL(`${this.config.apiBaseUrl}/api/sys/users/${userId}`);
    const accessToken: string = getBasicToken();
    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Request failed with status: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }

      return response.json();
    } catch (error) {
      return this.errorLogger.fromError(error).withMessage('Error fetching user').logAndThrowRouteError();
    }
  }

  async getCoworkerById(coworkerId: number): Promise<INexudusCoworker | null> {
    const url = new URL(`${this.config.apiBaseUrl}/api/spaces/coworkers/${coworkerId}`);
    const accessToken: string = getBasicToken();
    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });

      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Request failed with status: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }
      return await response.json();
    } catch (error) {
      return this.errorLogger.fromError(error).withMessage('Error fetching coworker').logAndThrowRouteError();
    }
  }

  async updateUser(user: INexudusUser): Promise<void> {
    const url = `${this.config.apiBaseUrl}/api/sys/users`;
    const startedAt = DateTime.now();
    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
        body: JSON.stringify(user),
      });

      await this.checker.readResponseOrThrow(response, startedAt);
    } catch (error) {
      return this.errorLogger.fromError(error).withMessage('Error updating user').logAndThrowRouteError();
    }
  }

  async runCoworkerCommand(
    userId: number,
    command: string,
    parameters: Record<string, string | number | boolean | null> = {},
  ) {
    // POST https://spaces.nexudus.com/api/spaces/coworkers/runcommand
    const url = `${this.config.apiBaseUrl}/api/spaces/coworkers/runcommand`;
    const accessToken: string = getBasicToken();
    const body = {
      Key: command,
      Ids: [userId],
      Parameters: Object.entries(parameters).map(([key, value]) => ({ Type: key, Value: value })),
    };

    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      const responseText = await response.text();
      if (!response.ok) {
        const message = tryParseJSON(responseText)?.Message || response.statusText;
        return this.errorLogger
          .fromMessage(`Request failed with status: ${response.status} ${message}`)
          .logAndThrowRouteError();
      }
      return JSON.parse(responseText) as { Message: string };
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error running coworker command')
        .logAndThrowRouteError();
    }
  }

  async getCoworkerExtraServices(coworkerId: string): Promise<{
    Records: IExtraServices[];
  }> {
    const url = new URL(`${this.config.apiBaseUrl}/api/billing/coworkerextraservices`);
    url.searchParams.append('CoworkerExtraService_Coworker', coworkerId);

    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });

      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Request failed with status: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }

      return await response.json();
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error fetching coworker extra services')
        .logAndThrowRouteError();
    }
  }

  async getUnpaidInvoicesByCoworkerId(coworkerId: number): Promise<INexudusInvoice[]> {
    const url = new URL(
      `${this.config.apiBaseUrl}/api/billing/coworkerinvoices?CoworkerInvoice_Coworker=${coworkerId}&CoworkerInvoice_Paid=false`,
    );
    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });

      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Request failed with status: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }
      const data = await response.json();
      return data.Records;
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error fetching unpaid invoices by coworkerId')
        .logAndThrowRouteError();
    }
  }

  async coworkerHasOutstandingInvoice(coworkerId: number): Promise<boolean> {
    const url = new URL(`${this.config.apiBaseUrl}/api/spaces/coworkers/${coworkerId}`);
    const accessToken: string = getBasicToken();
    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });

      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Failed to fetch coworker data: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }

      const coworkerData = await response.json();

      // 1. User has no outstanding invoices that are due
      const hasDueInvoices = coworkerData.HasDueInvoices;
      const contractIds = this.parseTariffIds(coworkerData.TariffId);

      // 2. User is not signed up to a non free plan
      const hasNonFreeContract = contractIds.some(id => id !== this.config.freePlanTariffId);

      // 3. User has no charges that have not yet been invoiced
      const coworkerExtraServices = await this.getCoworkerExtraServices(coworkerId.toString());

      const coworkerHasExtraServices = coworkerExtraServices.Records.some(
        (service: IExtraServices) => service.Price > 0 && !service.Invoiced,
      );

      const coworkerUnpaidInvoices = await this.getUnpaidInvoicesByCoworkerId(coworkerData.Id);
      const hasUnpaidInvoices = coworkerUnpaidInvoices.length > 0;

      return hasDueInvoices || hasNonFreeContract || coworkerHasExtraServices || hasUnpaidInvoices;
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error checking coworker outstanding invoices')
        .logAndThrowRouteError();
    }
  }

  async getBusinessData() {
    const url = `${this.config.apiBaseUrl}/api/sys/businesses`;
    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });

      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Failed to fetch business data: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }

      return await response.json();
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error fetching business data')
        .logAndThrowRouteError();
    }
  }

  async getBusinessByBusinessId(businessId: number) {
    const url = new URL(`${this.config.apiBaseUrl}/api/sys/businesses/${businessId}`);
    const accessToken: string = getBasicToken();
    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });

      if (!response.ok) {
        return this.errorLogger
          .fromMessage(
            `Failed to fetch business reception by coworker business id: ${response.status} ${response.statusText}`,
          )
          .logAndThrowRouteError();
      }

      return await response.json();
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error fetching business reception by coworker business id')
        .logAndThrowRouteError();
    }
  }

  async createCoworkerTask(payload: Record<string, unknown>) {
    const url = new URL(`${this.config.apiBaseUrl}/api/crm/coworkertasks`);
    const accessToken: string = getBasicToken();
    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Failed to create coworker task: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }

      return response;
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error creating coworker task')
        .logAndThrowRouteError();
    }
  }

  async getOpenCoworkerTasks(coworkerId: number): Promise<INexudusCoworkerTask[]> {
    const url = new URL(`${this.config.apiBaseUrl}/api/crm/coworkertasks`);
    url.searchParams.set('CoworkerTask_Coworker', coworkerId.toString());
    url.searchParams.set('CoworkerTask_Completed', 'false');
    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });

      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Failed to fetch coworker tasks: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }

      const { Records } = NexudusCoworkerTaskListResponseSchema.parse(await response.json());
      return Records;
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error fetching open coworker tasks')
        .logAndThrowRouteError();
    }
  }

  private parseTariffIds(tariffId: number | string | undefined): number[] {
    if (!tariffId) return [];

    if (typeof tariffId === 'number') {
      return [tariffId];
    }

    return tariffId.split(',').map(id => parseInt(id, 10));
  }

  async getBusinessNetworks(): Promise<INexudusBusinessNetworks> {
    const url = `${this.config.envBaseUrl}/en/business/networks?_depth=3`;
    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });

      if (!response.ok) {
        return this.errorLogger
          .fromMessage(`Request failed with status: ${response.status} ${response.statusText}`)
          .logAndThrowRouteError();
      }

      return await response.json();
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error fetching business networks')
        .logAndThrowRouteError();
    }
  }

  async getFormPageById(formPageId: number): Promise<IFormPageData | null> {
    const url = `${process.env.NEXUDUS_API_BASE_URL}/api/content/formpages/${formPageId}`;
    const response = await this.fetchHelper.fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Basic ${getBasicToken()}`,
      },
    });
    if (response.status === 404) {
      return null;
    }
    return this.checker.readResponseOrThrow<IFormPageData>(response, DateTime.now());
  }

  async searchFormPages(businessId?: number): Promise<IFormPageData[]> {
    const url = new URL(`${process.env.NEXUDUS_API_BASE_URL}/api/content/formpages`);

    url.searchParams.append('FormPage_Active', 'true');

    if (businessId) {
      url.searchParams.append('FormPage_Business', businessId.toString());
    }

    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        const e = new Error(`Request failed with status: ${response.status} ${response.statusText}`);
        logrocketService.logException(e);
        throw e;
      }

      const data = JSON.parse(responseText);
      return data.Records;
    } catch (error) {
      const e = new Error(`Error fetching form pages: ${error}`);
      logrocketService.logException(e);
      throw e;
    }
  }

  async searchFormQuestions(formPageId?: number): Promise<IFormQuestionsData[]> {
    const url = new URL(`${process.env.NEXUDUS_API_BASE_URL}/api/content/formpagequestions`);

    if (formPageId) {
      url.searchParams.append('FormPageQuestion_FormPage', formPageId.toString());
    }

    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        const e = new Error(`Request failed with status: ${response.status} ${response.statusText}`);
        logrocketService.logException(e);
        throw e;
      }

      const data = JSON.parse(responseText);
      return data.Records;
    } catch (error) {
      const e = new Error(`Error fetching form questions: ${error}`);
      logrocketService.logException(e);
      throw e;
    }
  }

  async addFormPageRequest(coworkerId: number, formPageId: number): Promise<IFormPageRequestResponse> {
    const url = `${process.env.NEXUDUS_API_BASE_URL}/api/content/formpagerequests`;
    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
        body: JSON.stringify({
          CoworkerId: coworkerId,
          FormPageId: formPageId,
          Submitted: true,
        }),
      });

      if (!response.ok) {
        const e = new Error(`Request failed with status: ${response.status} ${response.statusText}`);
        logrocketService.logException(e);
        throw e;
      }

      return await response.json();
    } catch (error) {
      const e = new Error(`Error adding form page request: ${error}`);
      logrocketService.logException(e);
      throw e;
    }
  }

  async uploadTempfile(fileBuffer: Buffer) {
    const url = 'https://storage.nexudus.com/api/content/uploads/tempfile';
    const accessToken: string = getBasicToken();

    const formContent = new FormData();
    formContent.append('file', fileBuffer, 'tempfile');

    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${accessToken}`,
        },
        body: formContent,
      });

      if (!response.ok) {
        const e = new Error(`Request failed with status: ${response.status} ${response.statusText}`);
        logrocketService.logException(e);
        throw e;
      }

      return await response.json();
    } catch (error) {
      const e = new Error(`Error uploading temporary image: ${error}`);
      logrocketService.logException(e);
      throw e;
    }
  }

  async addFormPageAnswer(formResponse: IFormAnswersResponse) {
    const url = `${process.env.NEXUDUS_API_BASE_URL}/api/content/formpageanswers`;
    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
        body: JSON.stringify(formResponse),
      });

      if (!response.ok) {
        const e = new Error(`Request failed with status: ${response.status} ${response.statusText}`);
        logrocketService.logException(e);
        throw e;
      }

      return await response.json();
    } catch (error) {
      const e = new Error(`Error submitting form response: ${error}`);
      logrocketService.logException(e);
      throw e;
    }
  }

  async updateFormPageRequest(coworkerId: number, formPageId: number, formRequestId: number) {
    const url = `${process.env.NEXUDUS_API_BASE_URL}/api/content/formpagerequests`;
    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${accessToken}`,
        },
        body: JSON.stringify({
          CoworkerId: coworkerId,
          FormPageId: formPageId,
          Id: formRequestId,
          Submitted: true,
          SentOn: moment().utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
        }),
      });

      if (!response.ok) {
        const e = new Error(`Request failed with status: ${response.status} ${response.statusText}`);
        logrocketService.logException(e);
        throw e;
      }

      return await response.json();
    } catch (error) {
      const e = new Error(`Error updating form page request: ${error}`);
      logrocketService.logException(e);
      throw e;
    }
  }

  async deleteFormPageRequest(formRequestId: number): Promise<void> {
    const url = `${process.env.NEXUDUS_API_BASE_URL}/api/content/formpagerequests/${formRequestId}`;
    await this.checker.requestWithErrorHandling<void>(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Basic ${getBasicToken()}`,
      },
    });
  }

  async getCoworkerForEmail(email: string): Promise<INexudusCoworker | undefined> {
    const url = new URL(`${this.config.apiBaseUrl}/api/spaces/coworkers`);
    url.searchParams.set('Coworker_Email', email);
    url.searchParams.set('page', '1');
    url.searchParams.set('size', '1');
    const accessToken: string = getBasicToken();

    try {
      const response = await this.fetchHelper.fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Basic ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status} ${response.statusText}`);
      }
      const responseText = await response.text();
      const body = JSON.parse(responseText);

      // Find a user with an email that matches the request
      if (body.Records.length === 0) return undefined;
      return body.Records.find((user: INexudusCoworker) => user.Email.toLowerCase() === email.toLowerCase());
    } catch (error) {
      return this.errorLogger
        .fromError(error)
        .withMessage('Error fetching user for email')
        .logAndThrowRouteError();
    }
  }
}
